<template>
  <section class="content-panel">
    <div class="section-head">
      <div>
        <h2>操作日志</h2>
        <p>管理员后台操作审计记录</p>
      </div>
      <div class="head-actions">
        <el-input
          v-model="filters.adminId"
          clearable
          placeholder="管理员ID"
          class="search-input"
          style="width: 120px"
          @keyup.enter="search"
        />
        <el-select v-model="filters.operationType" clearable placeholder="操作类型" style="width: 150px">
          <el-option
            v-for="item in operationOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
        <el-select v-model="filters.targetType" clearable placeholder="目标类型" style="width: 130px">
          <el-option label="客户" value="customer" />
          <el-option label="应用" value="customer_app" />
        </el-select>
        <el-date-picker
          v-model="filters.dateRange"
          type="daterange"
          value-format="YYYY-MM-DD"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          :shortcuts="dateShortcuts"
          :clearable="false"
          style="width: 280px"
        />
        <el-button type="primary" :loading="loading" @click="search">查询</el-button>
        <el-button @click="resetFilters">重置</el-button>
      </div>
    </div>

    <el-table v-loading="loading" :data="logs" stripe>
      <el-table-column type="index" label="#" width="56" />
      <el-table-column label="管理员" min-width="120">
        <template #default="{ row }">
          <div>{{ row.adminName || row.adminId }}</div>
          <div v-if="row.adminUsername" class="sub-text">ID {{ row.adminId }}</div>
        </template>
      </el-table-column>
      <el-table-column label="操作类型" min-width="120">
        <template #default="{ row }">
          <el-tag :type="operationTagType(row.operationType)" effect="light">
            {{ formatOperationType(row.operationType) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="目标" min-width="120">
        <template #default="{ row }">
          {{ formatTargetType(row.targetType) }}
          <span v-if="row.targetId != null" class="sub-text">#{{ row.targetId }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="detail" label="操作详情" min-width="200" show-overflow-tooltip />
      <el-table-column prop="ipAddress" label="IP" width="140" show-overflow-tooltip>
        <template #default="{ row }">{{ row.ipAddress || '-' }}</template>
      </el-table-column>
      <el-table-column prop="createdAt" label="操作时间" min-width="170">
        <template #default="{ row }">{{ formatDateTime(row.createdAt) }}</template>
      </el-table-column>
      <template #empty><el-empty description="暂无操作日志" /></template>
    </el-table>

    <div class="panel-footer">
      <el-pagination
        v-model:current-page="currentPage"
        v-model:page-size="pageSize"
        :page-sizes="[10, 20, 50, 100]"
        :total="total"
        layout="total, sizes, prev, pager, next, jumper"
        background
        @current-change="fetchLogs"
        @size-change="onSizeChange"
      />
    </div>
  </section>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import { ElMessage } from 'element-plus'
import { request } from '../services/adminApi'

const props = defineProps({
  admin: { type: Object, required: true }
})

const operationOptions = [
  { value: 'CREATE_CUSTOMER', label: '创建客户' },
  { value: 'UPDATE_CUSTOMER', label: '更新客户' },
  { value: 'TOGGLE_AD', label: '广告开关' },
  { value: 'UPDATE_STATUS', label: '状态变更' },
  { value: 'RECHARGE', label: '充值' },
  { value: 'MANUAL_ADJUST', label: '手动调整' },
  { value: 'REFUND', label: '退款' },
  { value: 'CREATE_APP', label: '创建应用' },
  { value: 'UPDATE_APP', label: '更新应用' },
  { value: 'DELETE_APP', label: '删除应用' }
]

const operationLabelMap = Object.fromEntries(operationOptions.map((item) => [item.value, item.label]))

const logs = ref([])
const loading = ref(false)
const currentPage = ref(1)
const pageSize = ref(20)
const total = ref(0)

const todayRange = () => {
  const today = new Date()
  const y = today.getFullYear()
  const m = String(today.getMonth() + 1).padStart(2, '0')
  const d = String(today.getDate()).padStart(2, '0')
  const value = `${y}-${m}-${d}`
  return [value, value]
}

const buildShortcutRange = (days = 0) => {
  const end = new Date()
  end.setHours(0, 0, 0, 0)
  const start = new Date(end)
  start.setDate(start.getDate() - days)
  return [start, end]
}

const dateShortcuts = [
  { text: '今日', value: () => buildShortcutRange(0) },
  {
    text: '昨日',
    value: () => {
      const day = new Date()
      day.setHours(0, 0, 0, 0)
      day.setDate(day.getDate() - 1)
      return [day, day]
    }
  },
  { text: '近7天', value: () => buildShortcutRange(6) },
  { text: '近30天', value: () => buildShortcutRange(29) }
]

const filters = reactive({
  adminId: '',
  operationType: '',
  targetType: '',
  dateRange: todayRange()
})

async function fetchLogs() {
  loading.value = true
  try {
    const params = new URLSearchParams({
      page: String(currentPage.value),
      size: String(pageSize.value)
    })
    if (filters.adminId) params.append('adminId', filters.adminId)
    if (filters.operationType) params.append('operationType', filters.operationType)
    if (filters.targetType) params.append('targetType', filters.targetType)
    if (filters.dateRange?.[0]) params.append('startDate', filters.dateRange[0])
    if (filters.dateRange?.[1]) params.append('endDate', filters.dateRange[1])

    const data = await request(
      `/api/admin/log/operation/list?${params}`,
      props.admin.token.value
    )
    logs.value = data?.records || []
    total.value = Number(data?.total || 0)
  } catch (error) {
    ElMessage.error(error.message || '查询操作日志失败')
  } finally {
    loading.value = false
  }
}

function search() {
  currentPage.value = 1
  fetchLogs()
}

function resetFilters() {
  filters.adminId = ''
  filters.operationType = ''
  filters.targetType = ''
  filters.dateRange = todayRange()
  currentPage.value = 1
  fetchLogs()
}

function onSizeChange() {
  currentPage.value = 1
  fetchLogs()
}

function formatOperationType(type) {
  return operationLabelMap[type] || type || '-'
}

function formatTargetType(type) {
  if (type === 'customer') return '客户'
  if (type === 'customer_app') return '应用'
  return type || '-'
}

function operationTagType(type) {
  if (type === 'CREATE_CUSTOMER' || type === 'CREATE_APP' || type === 'RECHARGE') return 'success'
  if (type === 'DELETE_APP' || type === 'REFUND' || type === 'UPDATE_STATUS') return 'danger'
  if (type === 'TOGGLE_AD' || type === 'MANUAL_ADJUST') return 'warning'
  return 'info'
}

function formatDateTime(dateTime) {
  if (!dateTime) return '-'
  return new Date(dateTime).toLocaleString('zh-CN')
}

onMounted(fetchLogs)
</script>

<style scoped>
.sub-text {
  color: var(--muted);
  font-size: 12px;
}
</style>
